/**
 * Metric value formatting — the shared numeric/time formatter used by every
 * charting primitive (LgChart axes + cursor, LgStatTile, LgGauge, LgSparkline
 * tooltips). One place decides how a byte count, a percent, a duration, a raw
 * count or a ratio reads, so the whole metrics surface is typographically
 * consistent.
 *
 * Deliberately dependency-free and pure (no DOM, no locale surprises) so it is
 * trivially unit-testable and safe to call from a canvas render loop.
 */

/** Semantic unit of a metric series. `none` = a bare number (compact-large). */
export type MetricUnit = 'bytes' | 'percent' | 'count' | 'ms' | 'ratio' | 'none';

export interface FormatOptions {
  /** Force a fixed number of decimals instead of the adaptive default. */
  decimals?: number;
  /** Placeholder for non-finite / missing values. Default `—`. */
  empty?: string;
}

const EMPTY = '—';

/** Round to `d` decimals and strip trailing zeros ("1.50" -> "1.5", "42.0" -> "42"). */
function trim(n: number, d: number): string {
  if (d <= 0) return String(Math.round(n));
  const s = n.toFixed(d);
  // Drop trailing zeros and a dangling decimal point.
  return s.replace(/\.?0+$/, '');
}

/** Fewer decimals as magnitude grows — keeps scaled values (1.5 GB) readable. */
function adaptiveDecimals(abs: number): number {
  if (abs >= 100) return 0;
  if (abs >= 10) return 1;
  return 2;
}

function nonFinite(n: number, empty: string): string | null {
  if (Number.isFinite(n)) return null;
  if (n === Infinity) return '∞';
  if (n === -Infinity) return '-∞';
  return empty;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

/**
 * If rounding `v` to `d` decimals reaches `threshold` (the next tier's
 * boundary — 1024 for bytes, 1000 for counts) and there is a next tier to
 * promote to, divide into it. Without this, the tier is picked from the
 * *unrounded* value, so e.g. 1048575 B (just under 1 MB) divides down to
 * 1023.999... KB, which then rounds for display to "1024 KB" instead of
 * promoting to "1 MB".
 */
function promoteOnRoundingOverflow(
  v: number,
  d: number,
  i: number,
  maxIndex: number,
  threshold: number,
): { v: number; i: number } {
  if (i >= maxIndex) return { v, i };
  if (Number(trim(v, d)) < threshold) return { v, i };
  return { v: v / threshold, i: i + 1 };
}

/** Human byte size, base-1024 ("1.5 GB", "512 B"). Signed; handles 0/±∞/NaN. */
export function formatBytes(n: number, opts: FormatOptions = {}): string {
  const bad = nonFinite(n, opts.empty ?? EMPTY);
  if (bad !== null) return bad;
  const sign = n < 0 ? '-' : '';
  let v = Math.abs(n);
  let i = 0;
  while (v >= 1024 && i < BYTE_UNITS.length - 1) {
    v /= 1024;
    i++;
  }
  let d = opts.decimals ?? (i === 0 ? 0 : adaptiveDecimals(v));
  ({ v, i } = promoteOnRoundingOverflow(v, d, i, BYTE_UNITS.length - 1, 1024));
  d = opts.decimals ?? (i === 0 ? 0 : adaptiveDecimals(v));
  return `${sign}${trim(v, d)} ${BYTE_UNITS[i]}`;
}

/** Percent value already in points ("42%", "4.2%"). Not a 0–1 ratio. */
export function formatPercent(n: number, opts: FormatOptions = {}): string {
  const bad = nonFinite(n, opts.empty ?? EMPTY);
  if (bad !== null) return bad;
  const abs = Math.abs(n);
  const d = opts.decimals ?? (abs >= 100 ? 0 : 1);
  return `${trim(n, d)}%`;
}

/** Duration given in milliseconds, scaled to µs / ms / s / min. */
export function formatMs(n: number, opts: FormatOptions = {}): string {
  const bad = nonFinite(n, opts.empty ?? EMPTY);
  if (bad !== null) return bad;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  let v: number;
  let unit: string;
  if (abs === 0) return '0 ms';
  if (abs < 1) {
    v = abs * 1000;
    unit = 'µs';
  } else if (abs < 1000) {
    v = abs;
    unit = 'ms';
  } else if (abs < 60_000) {
    v = abs / 1000;
    unit = 's';
  } else {
    v = abs / 60_000;
    unit = 'min';
  }
  const d = opts.decimals ?? adaptiveDecimals(v);
  return `${sign}${trim(v, d)} ${unit}`;
}

const COUNT_UNITS = ['', 'k', 'M', 'B', 'T'];

/** Compact count ("1.2k", "3.4M", "42"). Signed; handles ±∞/NaN. */
export function formatCount(n: number, opts: FormatOptions = {}): string {
  const bad = nonFinite(n, opts.empty ?? EMPTY);
  if (bad !== null) return bad;
  const sign = n < 0 ? '-' : '';
  let v = Math.abs(n);
  let i = 0;
  while (v >= 1000 && i < COUNT_UNITS.length - 1) {
    v /= 1000;
    i++;
  }
  let d = opts.decimals ?? (i === 0 ? (Number.isInteger(v) ? 0 : 1) : adaptiveDecimals(v));
  ({ v, i } = promoteOnRoundingOverflow(v, d, i, COUNT_UNITS.length - 1, 1000));
  d = opts.decimals ?? (i === 0 ? (Number.isInteger(v) ? 0 : 1) : adaptiveDecimals(v));
  return `${sign}${trim(v, d)}${COUNT_UNITS[i]}`;
}

/** Unitless ratio / factor, shown as a plain decimal ("0.85", "1.5", "128"). */
export function formatRatio(n: number, opts: FormatOptions = {}): string {
  const bad = nonFinite(n, opts.empty ?? EMPTY);
  if (bad !== null) return bad;
  const d = opts.decimals ?? adaptiveDecimals(Math.abs(n));
  return trim(n, d);
}

/** Dispatch to the right formatter for a semantic unit. */
export function formatMetric(
  n: number,
  unit: MetricUnit = 'none',
  opts: FormatOptions = {},
): string {
  switch (unit) {
    case 'bytes':
      return formatBytes(n, opts);
    case 'percent':
      return formatPercent(n, opts);
    case 'ms':
      return formatMs(n, opts);
    case 'count':
      return formatCount(n, opts);
    case 'ratio':
      return formatRatio(n, opts);
    case 'none':
    default:
      return formatCount(n, opts);
  }
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/** Compact local clock time for axis ticks / tooltips ("14:05", "14:05:30"). */
export function formatClockTime(ts: number, opts: { seconds?: boolean } = {}): string {
  if (!Number.isFinite(ts)) return EMPTY;
  const d = new Date(ts);
  const base = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return opts.seconds ? `${base}:${pad(d.getSeconds())}` : base;
}

/**
 * Terse relative age ("now", "12s", "3m", "2h", "4d") for stream/list rows.
 * `now` is injectable for deterministic tests.
 */
export function formatRelativeTime(ts: number, now: number = Date.now()): string {
  if (!Number.isFinite(ts)) return EMPTY;
  const diff = Math.max(0, now - ts);
  const s = Math.floor(diff / 1000);
  if (s < 1) return 'now';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
